/* iter-217 · Shared property-image fallback utilities.
 *
 * Provides one canonical onError handler + a calm SVG data URL so
 * broken / missing property thumbnails never surface as the browser's
 * default "broken image" icon, and never spam the console with
 * repeated 404s when an external CDN URL goes stale.
 *
 * Usage:
 *   import { PROPERTY_FALLBACK_SVG, onPropertyImgError } from "../lib/propertyImage";
 *   <img src={url} onError={onPropertyImgError} />
 *
 * Or use the <PropertyImage /> component which wraps this for you.
 */

// Calm slate-tinted placeholder — matches the bg-slate-100 / 200
// surfaces used across property cells so a fallback never feels like
// a hard error.
export const PROPERTY_FALLBACK_SVG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 240" width="320" height="240">
      <rect width="320" height="240" fill="#f1f5f9"/>
      <g fill="#cbd5e1" stroke="#cbd5e1" stroke-width="0">
        <path d="M160 88 L80 144 L80 196 L240 196 L240 144 Z" fill="#e2e8f0"/>
        <path d="M160 88 L80 144 L240 144 Z" fill="#cbd5e1"/>
        <rect x="148" y="160" width="24" height="36" fill="#cbd5e1"/>
        <rect x="100" y="156" width="24" height="20" fill="#cbd5e1"/>
        <rect x="196" y="156" width="24" height="20" fill="#cbd5e1"/>
      </g>
    </svg>`
  );

/** Inline onError handler for <img> tags rendering property images.
 *  Idempotent — swaps the src to the SVG fallback once, then detaches
 *  the handler so a broken fallback can't loop. */
export function onPropertyImgError(e) {
  const el = e?.currentTarget;
  if (!el) return;
  // Already showing the fallback — don't loop.
  if (el.dataset.propFallbackApplied === "1") return;
  el.dataset.propFallbackApplied = "1";
  el.src = PROPERTY_FALLBACK_SVG;
  // Calm down browser hover/title tooltip in case alt was missing.
  if (!el.alt) el.alt = "Property image unavailable";
}

// Genuine photo = uploaded in ChainFlow (data URL or own storage); stock-library URLs never count.
const STOCK_PHOTO_HOSTS = /(^|\.)(unsplash\.com|pexels\.com|pixabay\.com)$/i;
export function isUploadedPropertyPhoto(src) {
  const s = String(src || "").trim();
  if (!s) return false;
  if (/^data:image\//i.test(s)) return true;
  try {
    const u = new URL(s, window.location.origin);
    return /^https?:$/.test(u.protocol) && !STOCK_PHOTO_HOSTS.test(u.hostname);
  } catch {
    return false;
  }
}
